import {v4 as uuidv4} from 'uuid';
import {Bit, Component, Wisbit} from 'api/types/wisbitTypes';
import {
  ComponentDTO,
  ImageDTO,
  LessonDTO,
  PageDTO,
} from 'api/typesDTO/wisbitDTO';
import {decodeHtmlEntities} from 'api/utils';
import {Action} from 'redux';
import {ThunkDispatch} from 'redux-thunk';
import {RootState} from 'store/selectors';
import {COMPONENT_DTO_TYPES} from 'api/constants';
import {COMPONENT_TYPES} from 'containers/WisbitPage/constants';
import {convertTagsToTagDTOArray} from './tagConverters';

export const convertComponentDTOToBitComponent = async ({
  id: _id,
  type,
  ...rest
}: ComponentDTO): Promise<Component> => {
  const id = _id || uuidv4();

  switch (type) {
    case COMPONENT_DTO_TYPES.TEXT:
      return {
        id,
        type: COMPONENT_TYPES.TEXT,
        value: decodeHtmlEntities(rest.text),
      };
    case COMPONENT_DTO_TYPES.IMAGE: {
      return {
        id,
        type: COMPONENT_TYPES.IMAGE,
        imageId: rest.image?.id,
      };
    }
    case COMPONENT_DTO_TYPES.VIDEO:
      return {
        id,
        type: COMPONENT_TYPES.VIDEO,
        value: rest.video?.url,
      };
    case COMPONENT_DTO_TYPES.LINK:
      return {
        id,
        type: COMPONENT_TYPES.LINK,
        value: rest.link?.url,
        caption: rest.link?.caption,
      };
    case COMPONENT_DTO_TYPES.MEETING: {
      return {
        id,
        type: COMPONENT_TYPES.MEETING,
        dateValue: new Date(rest.meeting?.date || 0),
      };
    }
    case COMPONENT_DTO_TYPES.TEXT_QUESTION:
      return {
        id,
        value: 'vraag',
        type: COMPONENT_TYPES.OPEN_QUESTION,
        feedback: rest.question?.explanation,
      };
    case COMPONENT_DTO_TYPES.SINGLE_CHOICE_QUESTION: {
      return {
        id,
        value: 'vraag',
        type: COMPONENT_TYPES.MULTIPLE_CHOICES,
        feedback: rest.question?.explanation,
        choices: rest.question?.options?.map(
          ({text, is_correct: isAnswer}, index) => ({
            key: index,
            text: text || '',
            isAnswer,
          })
        ),
      };
    }
    default:
      return {id};
  }
};

export const convertBitComponentToComponentDTO = async ({
  type,
  value,
  dateValue,
  choices,
  imageId,
  feedback,
  caption,
}: Component): Promise<ComponentDTO> => {
  switch (type) {
    case COMPONENT_TYPES.TEXT: {
      return {
        type: COMPONENT_DTO_TYPES.TEXT,
        text: value,
      };
    }
    case COMPONENT_TYPES.IMAGE: {
      return {
        type: COMPONENT_DTO_TYPES.IMAGE,
        image: {id: imageId || ''},
      };
    }
    case COMPONENT_TYPES.VIDEO: {
      return {
        type: COMPONENT_DTO_TYPES.VIDEO,
        video: {
          url: value,
        },
      };
    }
    case COMPONENT_TYPES.LINK: {
      return {
        type: COMPONENT_DTO_TYPES.LINK,
        link: {
          caption: caption || value,
          url: value,
        },
      };
    }
    case COMPONENT_TYPES.OPEN_QUESTION: {
      return {
        type: COMPONENT_DTO_TYPES.TEXT_QUESTION,
        question: {
          explanation: feedback,
        },
      };
    }
    case COMPONENT_TYPES.MULTIPLE_CHOICES: {
      return {
        type: COMPONENT_DTO_TYPES.SINGLE_CHOICE_QUESTION,
        question: {
          explanation: feedback,
          options: choices
            ? choices.map(({text, isAnswer}) => ({
                text,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                is_correct: isAnswer,
              }))
            : [],
        },
      };
    }
    case COMPONENT_TYPES.MEETING: {
      return {
        type: COMPONENT_DTO_TYPES.MEETING,
        meeting: {
          date: dateValue || new Date(),
        },
      };
    }
    default:
      return {};
  }
};

export const convertPageDTOToBit = async (pageDTO: PageDTO): Promise<Bit> => {
  const {id, duration_estimate: durationEstimate, title, components} = pageDTO;

  const newComponents = [];
  if (components) {
    for (let i = 0; i < components.length; i++) {
      const newComponent = await convertComponentDTOToBitComponent(
        components[i]
      );

      newComponents.push(newComponent);
    }
  }

  return {
    id: id || uuidv4(),
    title,
    duration: (durationEstimate || 0) / 60,
    components: newComponents,
  };
};

export const convertBitToPageDTO = async ({
  title,
  duration,
  components,
}: Bit): Promise<PageDTO> => {
  const newComponents = [];
  if (components) {
    for (const component of components) {
      const newComponent = await convertBitComponentToComponentDTO(component);
      newComponents.push(newComponent);
    }
  }

  const page = {
    title: title || '',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    duration_estimate: duration ? duration * 60 : 0,
    components: newComponents,
  };

  return page;
};

export const convertWisbitToLessonDTO = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: ThunkDispatch<RootState, any, Action<string>>,
  wisbit: Wisbit
): Promise<LessonDTO> => {
  const {title, description, thumbnailId, bits, tags} = wisbit;

  const image: ImageDTO = {id: thumbnailId || ''};

  // Convert bits to pages.
  const pages: PageDTO[] = [];
  if (bits) {
    for (const bit of bits) {
      pages.push(await convertBitToPageDTO(bit));
    }
  }

  // Construct lesson DTO and return.
  return {
    image,
    title,
    description,
    pages,
    tags: convertTagsToTagDTOArray(tags),
  };
};

export const convertLessonDTOToWisbit = async (
  lessonDTO: LessonDTO
): Promise<Wisbit> => {
  const {id, image, title, description, pages, tags, ...rest} = lessonDTO;

  const bits: Bit[] = [];
  if (pages) {
    for (let i = 0; i < pages.length; i++) {
      const bit = await convertPageDTOToBit(pages[i]);
      bits.push(bit);
    }
  }

  return {
    id,
    title,
    description: decodeHtmlEntities(description),
    thumbnailId: image?.id,
    bits,
    tags: (tags || []).map(t => ({
      id: t.id,
      name: t.name,
    })),
    ...rest,
  };
};
